import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';

const HeaderContainer = styled(motion.header)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: fixed;
  z-index: 1000;
  top: 8px;
  padding: 0 1rem;
  /* Navbar sized up ~15% relative to the rest of the site. Tune this value. */
  zoom: 1.15;
  opacity: ${({ $scrolled }) => ($scrolled ? 1 : 0.9)};
  transition: opacity 0.3s ease;

  nav {
    display: flex;
    position: static;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    padding: 0.4rem 0.5rem;
    background: ${({ theme }) => theme.navBackground};
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid ${({ theme }) => theme.borderColor};
    border-radius: 16px;
    overflow: hidden;
  }

  @media (max-width: 800px) {
    top: max(12px, env(safe-area-inset-top));
    left: 12px;
    right: 12px;
    width: auto;
    padding: 0;
    justify-content: flex-end;
    zoom: 1;

    nav {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      width: min(230px, calc(100vw - 24px));
      flex-direction: column;
      align-items: stretch;
      gap: 0.2rem;
      padding: 0.45rem;
      border-radius: 14px;
      box-shadow: ${({ theme }) => theme.shadow};
      opacity: ${({ $menuOpen }) => ($menuOpen ? 1 : 0)};
      visibility: ${({ $menuOpen }) => ($menuOpen ? 'visible' : 'hidden')};
      pointer-events: ${({ $menuOpen }) => ($menuOpen ? 'auto' : 'none')};
      transform: ${({ $menuOpen }) => ($menuOpen ? 'translateY(0)' : 'translateY(-8px)')};
      transition:
        opacity 0.2s ease,
        visibility 0.2s ease,
        transform 0.2s ease;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;

  @media (max-width: 800px) {
    min-width: 96px;
    min-height: 44px;
    padding: 0 0.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.navBackground};
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid ${({ theme }) => theme.borderColor};
    border-radius: 12px;
    box-shadow: ${({ theme }) => theme.shadow};
    font: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.accentPrimary};
      outline-offset: 3px;
    }
  }
`;

const MenuIcon = styled.span`
  position: relative;
  width: 18px;
  height: 14px;
  display: inline-block;

  &::before,
  &::after,
  span {
    content: '';
    position: absolute;
    left: 0;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: ${({ theme }) => theme.accentPrimary};
    transition: transform 0.2s ease, top 0.2s ease, opacity 0.2s ease;
  }

  &::before {
    top: ${({ $open }) => ($open ? '6px' : '0')};
    transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'none')};
  }

  span {
    top: 6px;
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }

  &::after {
    top: ${({ $open }) => ($open ? '6px' : '12px')};
    transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'none')};
  }
`;

const NavLink = styled(Link)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 1rem;
  padding: 0.5rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme, $isActive }) => $isActive ? theme.text : theme.textMuted};
  background: ${({ theme, $isActive }) => $isActive ? theme.surfaceHover : 'transparent'};
  border-radius: 10px;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
  letter-spacing: 0.01em;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.surfaceHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accentPrimary};
    outline-offset: 1px;
  }

  @media (max-width: 800px) {
    width: 100%;
    min-height: 44px;
    margin: 0;
    justify-content: flex-start;
    padding: 0.65rem 0.85rem;
    font-size: 0.9rem;
  }
`;

const sections = ['home', 'projects', 'skills', 'education', 'aboutMe'];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 800);
  const headerRef = useRef(null);

  const detectSection = useCallback(() => {
    const scrollY = window.scrollY + 150;

    for (let i = sections.length - 1; i >= 0; i--) {
      const el = document.getElementById(sections[i]);
      // Use getBoundingClientRect so zoomed sections (Skills/Education) report
      // their true rendered position; offsetTop is unreliable under CSS zoom.
      if (el && el.getBoundingClientRect().top + window.scrollY <= scrollY) {
        setActiveSection(sections[i]);
        return;
      }
    }
    setActiveSection('home');
  }, []);

  useEffect(() => {
    // Throttle to one layout read per frame; scroll events can fire faster
    // than the display refreshes and detectSection forces layout.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        detectSection();
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    detectSection();
    return () => window.removeEventListener('scroll', onScroll);
  }, [detectSection]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 800;
      setIsMobile(mobile);
      if (!mobile) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  const navOffset = (desktopOffset) => (isMobile ? -72 : desktopOffset);
  const closeMenu = () => setMenuOpen(false);
  const activateWithKeyboard = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  return (
    <HeaderContainer
      ref={headerRef}
      $scrolled={scrolled}
      $menuOpen={menuOpen}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <MobileMenuButton
        type="button"
        aria-expanded={menuOpen}
        aria-controls="primary-navigation"
        aria-label={`${menuOpen ? 'Close' : 'Open'} navigation menu`}
        onClick={() => setMenuOpen((open) => !open)}
      >
        Menu
        <MenuIcon $open={menuOpen} aria-hidden="true"><span /></MenuIcon>
      </MobileMenuButton>
      <nav id="primary-navigation" aria-label="Primary navigation">
        <NavLink
          to="home"
          smooth={true}
          duration={500}
          offset={navOffset(0)}
          $isActive={activeSection === 'home'}
          aria-current={activeSection === 'home' ? 'page' : undefined}
          tabIndex={0}
          onClick={closeMenu}
          onKeyDown={activateWithKeyboard}
        >
          Home
        </NavLink>
        <NavLink
          to="projects"
          smooth={true}
          duration={500}
          offset={navOffset(20)}
          $isActive={activeSection === 'projects'}
          aria-current={activeSection === 'projects' ? 'page' : undefined}
          tabIndex={0}
          onClick={closeMenu}
          onKeyDown={activateWithKeyboard}
        >
          Projects
        </NavLink>
        <NavLink
          to="skills"
          smooth={true}
          duration={500}
          offset={navOffset(20)}
          $isActive={activeSection === 'skills'}
          aria-current={activeSection === 'skills' ? 'page' : undefined}
          tabIndex={0}
          onClick={closeMenu}
          onKeyDown={activateWithKeyboard}
        >
          Skills
        </NavLink>
        <NavLink
          to="education"
          smooth={true}
          duration={500}
          offset={navOffset(10)}
          $isActive={activeSection === 'education'}
          aria-current={activeSection === 'education' ? 'page' : undefined}
          tabIndex={0}
          onClick={closeMenu}
          onKeyDown={activateWithKeyboard}
        >
          Education
        </NavLink>
        <NavLink
          to="aboutMe"
          smooth={true}
          duration={500}
          offset={navOffset(-10)}
          $isActive={activeSection === 'aboutMe'}
          aria-current={activeSection === 'aboutMe' ? 'page' : undefined}
          tabIndex={0}
          onClick={closeMenu}
          onKeyDown={activateWithKeyboard}
        >
          About
        </NavLink>
      </nav>
    </HeaderContainer>
  );
};

export default Header;
